import { gql } from '@apollo/client';

const ACCESSIBILITY_FINDING_GROUP_FIELDS = gql`
  fragment AccessibilityFindingPageGroupFields on AccessibilityFindingGroup {
    id
    ruleId
    status
    criterionIds
    classification
    occurrenceCount
    pageCount
    lastSeenAt
    description
    fixInstructions
    helpUrl
    newCount
    recurringCount
    regressedCount
    occurrences {
      id
      status
      providerProblemNumber
      pageUrl
      pageTitle
      pageType
      evidenceKind
      detectionState
      disposition
      firstSeenAt
      lastSeenAt
      description
      fixInstructions
      helpUrl
      affectedHtml
      cssSelectors
      viewport { name source width height deviceScaleFactor }
      screenshot {
        status reason source capturedAt storageUrl contentType byteSize pageSize
        documentPageNumber
        annotationStatus cookieBannerHandling
        viewport { name source width height deviceScaleFactor }
      }
      annotations { selector status boundingBox }
      source
      linkUrl
      httpStatus
      specification
      clause
      testNumber
      documentPageNumber
      structurePath
      objectId
      boundingBox
      validator
      validatorVersion
      profile
      ruleMapVersion
      contentHash
      hasRawReport
      pdfScanEvidenceId
    }
  }
`;

export const GET_ACCESSIBILITY_REVIEW = gql`
  ${ACCESSIBILITY_FINDING_GROUP_FIELDS}
  query GetDataSourceAccessibilityReview(
    $id: ID!
    $includeOps: Boolean!
    $includeResult: Boolean!
    $includeManualReview: Boolean!
    $includeReviewUnits: Boolean!
    $includeControls: Boolean!
    $includeScanHistory: Boolean!
    $includeWebsiteChecks: Boolean!
    $includeWebhookDeliveries: Boolean!
    $includePageCounts: Boolean!
    $includeScanOverview: Boolean!
    $includeTrend: Boolean!
    $includeSyncs: Boolean!
    $pageType: AccessibilityPageTypeFilter
  ) {
    dataSource(id: $id) {
      id
      website {
        id
        url
        additionalUrls
        excludedUrls
        brokenLinksEnabled
        checkInterval
        checks(first: 10, orderBy: createdAt_DESC) @include(if: $includeWebsiteChecks) {
          id
          type
          createdAt
          updatedAt
          entryUrl
          status
          recursive
        }
      }
      accessibilityReviewScopes {
        id
        provider
        providerSiteId
        baselineId
        baselineVersion
        active
        automaticRunsEnabled
        pdfScanningEnabled
        automaticImportConfigured
        syncs(first: 10, orderBy: createdAt_DESC) @include(if: $includeSyncs) {
          id
          createdAt
          completedAt
          status
          syncComplete
          error
        }
        webhookDeliveries(first: 10) @include(if: $includeWebhookDeliveries) {
          id
          createdAt
          provider
          classification
        }
        scanHistory @include(if: $includeScanHistory) {
          id
          source
          scanType
          trigger
          reason
          status
          startedAt
          completedAt
          pagesChecked
          findings
          pdfBatchProgress {
            queued
            completed
            failed
            skipped
          }
          historicalResults
        }
        pageInventory(pageType: ALL, first: 0) @include(if: $includePageCounts) {
          totalCount
          htmlCount
          pdfCount
        }
        scanOverview @include(if: $includeScanOverview) {
          inventoriedPageCount
          htmlPageCount
          pdfPageCount
          pageLimit
          pagesWithObservations
          reviewUnitCount
          completedReviewUnitCount
          sources {
            source
            observationCount
            latestRun {
              id
              status
              createdAt
              completedAt
              error
            }
            latestCompletedRun {
              id
              status
              createdAt
              completedAt
            }
          }
        }
        reviewResult(pageType: $pageType, includeManualReview: $includeManualReview, includeTrend: $includeTrend) @include(if: $includeResult) {
          baselineId
          baselineVersion
          latestSync {
            id
            createdAt
            completedAt
            status
            syncComplete
            providerReportedProblemCount
            retrievedProblemCount
            visibleProblemCount
            error
          }
          latestCompletedSyncId
          summary {
            controlCount
            automatedFailureControlCount
            manualReviewControlCount
            notStartedControlCount
            findingGroupCount
            providerProblemCount
            pdfObservationCount
            occurrenceCount
            unmappedFindingCount
            reviewUnitCount
            completedReviewUnitCount
            inProgressReviewUnitCount
            blockedReviewUnitCount
          }
          overview {
            activeIssueCount
            affectedPageCount
            allAffectedPageCount
            newIssueCount30d
            resolvedIssueCount30d
            hasComparison
            score {
              value
              status
              automaticScore
              trendScore
              manualReviewScore
              manualReviewCoverage
            }
            trend {
              syncId
              observedAt
              activeIssueCount
            }
          }
          reviewUnits @include(if: $includeReviewUnits) {
            id
            title
            kind
            status
            workStatus
            completedAt
            completedBy
            reviewedControlCount
            controlCount
            rationale
            manualControls {
              criterionId
              status
              pageUrl
              pageType
              observedAt
              rationale
            }
            urls {
              id
              url
              normalizedUrl
              representative
              pageType
            }
          }
          controls @include(if: $includeControls) {
            id
            title
            level
            principle
            guideline
            appliesWhen
            manualCheck
            description
            understandingUrl
            automationPotential
            applicablePageTypes
            status
            evidenceCount
            findingCount
            manualTargetCount
            manualReviewedCount
            manualPassCount
            manualFailCount
            manualBlockedCount
            lastObservedAt
          }
          findingSuites {
            suiteType
            findingGroupCount
            providerProblemCount
            pdfObservationCount
            occurrenceCount
            findingGroups {
              ...AccessibilityFindingPageGroupFields
            }
          }
        }
        webhookUrl @include(if: $includeOps)
      }
    }
  }
`;

export const GET_ACCESSIBILITY_FINDING_PAGE = gql`
  ${ACCESSIBILITY_FINDING_GROUP_FIELDS}
  query GetAccessibilityFindingPage($dataSourceId: ID!, $syncId: ID, $first: Int!, $skip: Int!, $pageType: AccessibilityPageTypeFilter, $includeHiddenDispositions: Boolean) {
    dataSource(id: $dataSourceId) {
      id
      accessibilityReviewScopes {
        id
        findingPage(syncId: $syncId, first: $first, skip: $skip, pageType: $pageType, includeHiddenDispositions: $includeHiddenDispositions) {
          latestSyncId
          hasMore
          nextSkip
          findingSuites {
            suiteType
            findingGroupCount
            providerProblemCount
            pdfObservationCount
            occurrenceCount
            findingGroups {
              ...AccessibilityFindingPageGroupFields
            }
          }
        }
      }
    }
  }
`;

export const SQUIDLER_FINDING_PAGE_SIZE = 100;
export const PAGE_INVENTORY_PAGE_SIZE = 100;

export const GET_ACCESSIBILITY_PAGE_INVENTORY = gql`
  query GetAccessibilityPageInventory($dataSourceId: ID!, $pageType: AccessibilityPageTypeFilter, $first: Int!, $skip: Int!) {
    dataSource(id: $dataSourceId) {
      id
      accessibilityReviewScopes {
        id
        pageInventory(pageType: $pageType, first: $first, skip: $skip) {
          totalCount
          htmlCount
          pdfCount
          pdfScanningEnabled
          pageLimit
          hasMore
          nextSkip
          pages {
            id
            pageType
            classificationStatus
            pageUrl
            locations {
              id
              requestedUrl
              finalUrl
              currentlyAvailable
              availabilityState
            }
            referringPageCount
            referringPages {
              pageId
              url
              discoveryKind
            }
            latestPdfScanEvidence {
              id
              status
              contentHash
              completedAt
              validator
              validatorVersion
              profile
              ruleMapVersion
              errorCode
            }
          }
        }
      }
    }
  }
`;

export const GET_WEBSITE_CHECK_STATUSES = gql`
  query GetAccessibilityWebsiteCheckStatuses($websiteId: ID!) {
    website(id: $websiteId) {
      id
      activeChecks(first: 30) {
        id
        status
        updatedAt
      }
    }
  }
`;

export const GET_ACCESSIBILITY_DASHBOARD = gql`
  query GetDataSourceAccessibilityDashboard($id: ID!) {
    dataSource(id: $id) {
      id
      accessibilityReviewScopes {
        id
        scanOverview {
          sources {
            source
            observationCount
            latestCompletedRun {
              completedAt
              createdAt
            }
          }
        }
      }
    }
  }
`;

export const SETUP_ACCESSIBILITY_REVIEW = gql`
  mutation SetupDataSourceAccessibilityReview($dataSourceId: ID!) {
    setupDataSourceAccessibilityReview(dataSourceId: $dataSourceId) {
      id
    }
  }
`;

export const ENABLE_ACCESSIBILITY_AUTOMATION = gql`
  mutation EnableDataSourceAccessibilityAutomation($dataSourceId: ID!) {
    enableDataSourceAccessibilityAutomation(dataSourceId: $dataSourceId) {
      id
    }
  }
`;

export const VERIFY_ACCESSIBILITY_WEBHOOK = gql`
  mutation VerifyDataSourceAccessibilityWebhook($dataSourceId: ID!) {
    verifyDataSourceAccessibilityWebhook(dataSourceId: $dataSourceId) {
      id
    }
  }
`;

export const SYNC_ACCESSIBILITY_REVIEW = gql`
  mutation SyncDataSourceAccessibilityReview($dataSourceId: ID!) {
    syncDataSourceAccessibilityReview(dataSourceId: $dataSourceId) {
      id
    }
  }
`;

export const START_ACCESSIBILITY_SCAN = gql`
  mutation StartDataSourceAccessibilityScan($dataSourceId: ID!, $pages: [String!]) {
    startDataSourceAccessibilityScan(dataSourceId: $dataSourceId, pages: $pages) {
      id
    }
  }
`;

export const CREATE_WEBSITE_CHECK = gql`
  mutation CreateAccessibilityWebsiteCheck($websiteId: ID!, $input: WebsiteCheckInput!) {
    createWebsiteCheck(websiteId: $websiteId, input: $input) {
      id
      status
    }
  }
`;

export const GET_WEBSITE_CHECK_EVIDENCE = gql`
  query GetWebsiteCheckEvidence($websiteCheckId: ID!, $pageUrl: String!) {
    websiteCheckEvidence(websiteCheckId: $websiteCheckId, pageUrl: $pageUrl) {
      status
      reason
      source
      capturedAt
      storageUrl
      contentType
      byteSize
      pageSize
      annotationStatus
      cookieBannerHandling
      viewport {
        name
        source
        width
        height
        deviceScaleFactor
      }
    }
  }
`;

export const GET_PDF_SCAN_PREVIEW = gql`
  query GetAccessibilityPdfScanPreview($dataSourceId: ID!, $scanEvidenceId: ID!) {
    dataSource(id: $dataSourceId) {
      id
      accessibilityReviewScopes {
        id
        pdfScanPreview(id: $scanEvidenceId) {
          status
          reason
          source
          capturedAt
          storageUrl
          contentType
          byteSize
          documentPageNumber
        }
      }
    }
  }
`;

export const ACCEPT_ACCESSIBILITY_REVIEW_COVERAGE = gql`
  mutation AcceptDataSourceAccessibilityReviewCoverage($dataSourceId: ID!) {
    acceptDataSourceAccessibilityReviewCoverage(dataSourceId: $dataSourceId) {
      id
    }
  }
`;

export const SET_ACCESSIBILITY_FINDING_DISPOSITION = gql`
  mutation SetDataSourceAccessibilityFindingDisposition(
    $dataSourceId: ID!
    $occurrenceIds: [ID!]!
    $disposition: AccessibilityFindingDisposition!
  ) {
    setDataSourceAccessibilityFindingDisposition(
      dataSourceId: $dataSourceId
      occurrenceIds: $occurrenceIds
      disposition: $disposition
    ) {
      id
      disposition
    }
  }
`;

export const RECORD_ACCESSIBILITY_MANUAL_REVIEW = gql`
  mutation RecordDataSourceAccessibilityManualReview(
    $dataSourceId: ID!
    $reviewUnitId: ID!
    $criterionId: String!
    $pageUrl: String!
    $status: String!
    $rationale: String!
  ) {
    recordDataSourceAccessibilityManualReview(
      dataSourceId: $dataSourceId
      reviewUnitId: $reviewUnitId
      criterionId: $criterionId
      pageUrl: $pageUrl
      status: $status
    rationale: $rationale
    ) {
      id
      criterionId
      pageUrl
      pageType
      status
      observedAt
      rationale
    }
  }
`;

export const UPDATE_ACCESSIBILITY_AUTOMATIC_RUNS = gql`
  mutation UpdateDataSourceAccessibilityAutomaticRuns($dataSourceId: ID!, $automaticRunsEnabled: Boolean!) {
    updateDataSourceAccessibilityAutomaticRuns(dataSourceId: $dataSourceId, automaticRunsEnabled: $automaticRunsEnabled) {
      id
      automaticRunsEnabled
    }
  }
`;

export const UPDATE_ACCESSIBILITY_PDF_SCANNING = gql`
  mutation UpdateDataSourceAccessibilityPdfScanning($dataSourceId: ID!, $pdfScanningEnabled: Boolean!) {
    updateDataSourceAccessibilityPdfScanning(dataSourceId: $dataSourceId, pdfScanningEnabled: $pdfScanningEnabled) {
      id
      pdfScanningEnabled
    }
  }
`;

export const UPDATE_WEBSITE_BROKEN_LINKS = gql`
  mutation UpdateWebsiteBrokenLinks($id: ID!, $input: WebsiteInput!) {
    updateWebsite(id: $id, input: $input) {
      id
      brokenLinksEnabled
    }
  }
`;

export const UPDATE_ACCESSIBILITY_REVIEW_UNIT_STATUS = gql`
  mutation UpdateDataSourceAccessibilityReviewUnitStatus(
    $dataSourceId: ID!
    $reviewUnitId: ID!
    $workStatus: String!
  ) {
    updateDataSourceAccessibilityReviewUnitStatus(
      dataSourceId: $dataSourceId
      reviewUnitId: $reviewUnitId
      workStatus: $workStatus
    ) {
      id
      workStatus
      completedAt
      completedBy
    }
  }
`;
